import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiCheckCircle,
  FiGrid,
  FiList,
  FiLoader,
  FiSearch,
  FiSliders,
  FiX,
} from "react-icons/fi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { categories, topWebsites, websites } from "../../data/fakeData";
import FilterSidebar from "./FilterSidebar";
import WebsiteCard from "./WebsiteCard";

const ITEMS_PER_PAGE = 6;

/* ─── Skeleton Card ──────────────────────────── */
const SkeletonCard = ({ viewMode }) => {
  if (viewMode === "list") {
    return (
      <div className="card flex items-center gap-4 px-5 py-4 animate-pulse">
        <div className="w-12 h-12 rounded-xl bg-slate-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 bg-slate-200 rounded-lg w-2/5" />
          <div className="h-2.5 bg-slate-100 rounded-lg w-4/5" />
        </div>
        <div className="hidden md:flex items-center gap-2">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="w-3 h-3 rounded-full bg-slate-200" />
            ))}
          </div>
          <div className="h-3 bg-slate-200 rounded w-6" />
        </div>
        <div className="hidden lg:block h-5 w-16 bg-slate-100 rounded-lg" />
        <div className="h-3 w-3 rounded bg-slate-200" />
      </div>
    );
  }

  return (
    <div className="card overflow-hidden animate-pulse">
      <div className="h-44 bg-slate-200" />
      <div className="p-4 space-y-3">
        <div className="h-3.5 bg-slate-200 rounded-lg w-2/3" />
        <div className="space-y-1.5">
          <div className="h-2.5 bg-slate-100 rounded w-full" />
          <div className="h-2.5 bg-slate-100 rounded w-5/6" />
        </div>
        <div className="pt-3 border-t-2 border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <div
                  key={s}
                  className="w-2.5 h-2.5 rounded-full bg-slate-200"
                />
              ))}
            </div>
            <div className="h-2.5 w-6 bg-slate-200 rounded" />
          </div>
          <div className="h-4 w-16 bg-slate-100 rounded-md" />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════ */
const ListingPage = ({ isTop50 = false }) => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSearch = searchParams.get("search") || "";

  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("rating");
  const [showFilters, setShowFilters] = useState(false);
  const [minRating, setMinRating] = useState(0);

  /* Infinite scroll state */
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const loaderRef = useRef(null);

  const category = slug ? categories.find((c) => c.slug === slug) : null;

  /* All filtered sites */
  const allFilteredSites = useMemo(() => {
    let sites = isTop50 ? [...topWebsites] : [...websites];
    if (slug) sites = sites.filter((s) => s.categorySlug === slug);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      sites = sites.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q) ||
          s.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    if (minRating > 0) sites = sites.filter((s) => s.rating >= minRating);
    switch (sortBy) {
      case "rating":
        sites.sort((a, b) => b.rating - a.rating);
        break;
      case "reviews":
        sites.sort((a, b) => b.totalReviews - a.totalReviews);
        break;
      case "visits":
        sites.sort((a, b) => {
          const parse = (v) => parseFloat(v.replace("M", "")) * 1000000;
          return parse(b.monthlyVisits) - parse(a.monthlyVisits);
        });
        break;
      case "name":
        sites.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    return sites;
  }, [slug, searchQuery, sortBy, minRating, isTop50]);

  /* Visible sites based on current page */
  const visibleSites = useMemo(
    () => allFilteredSites.slice(0, page * ITEMS_PER_PAGE),
    [allFilteredSites, page],
  );

  /* Reset page when filters change */
  useEffect(() => {
    setPage(1);
    setAllLoaded(false);
  }, [slug, searchQuery, sortBy, minRating, isTop50]);

  /* Intersection Observer for infinite scroll */
  const handleObserver = useCallback(
    (entries) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        !loadingMore &&
        !allLoaded &&
        visibleSites.length < allFilteredSites.length
      ) {
        setLoadingMore(true);
        setTimeout(() => {
          setPage((prev) => {
            const next = prev + 1;
            if (next * ITEMS_PER_PAGE >= allFilteredSites.length) {
              setAllLoaded(true);
            }
            return next;
          });
          setLoadingMore(false);
        }, 600);
      }
    },
    [loadingMore, allLoaded, visibleSites.length, allFilteredSites.length],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "120px",
      threshold: 0,
    });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  /* Check if all loaded on initial */
  useEffect(() => {
    if (allFilteredSites.length <= ITEMS_PER_PAGE) {
      setAllLoaded(true);
    }
  }, [allFilteredSites.length]);

  const title = isTop50
    ? "Top 50 Websites"
    : category
      ? category.name
      : initialSearch
        ? `Results for "${initialSearch}"`
        : "All Websites";

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/websites?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const cols =
    viewMode === "grid"
      ? showFilters
        ? "grid-cols-1 md:grid-cols-2"
        : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
      : "grid-cols-1";

  return (
    <div className="section-tinted min-h-screen">
      <div className="container-main py-10 lg:py-14">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          {category && (
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center font-extrabold text-base mb-4"
              style={{
                backgroundColor: category.color + "15",
                color: category.color,
              }}
            >
              {category.name.charAt(0)}
            </div>
          )}
          <h1 className="page-title mb-2">{title}</h1>
          <p className="text-base text-slate-500 leading-relaxed max-w-xl">
            {category?.description ||
              (isTop50
                ? "The highest rated websites — ranked by our community."
                : initialSearch
                  ? `Showing results for "${initialSearch}"`
                  : "Browse and discover the best websites in our directory.")}
          </p>
        </motion.div>

        {/* ── Search Bar ── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-5"
        >
          <form onSubmit={handleSearchSubmit} className="relative max-w-2xl">
            <FiSearch
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${category ? category.name.toLowerCase() : "all"} websites...`}
              className="w-full bg-white border-2 border-slate-200 rounded-xl pl-11 pr-24 py-3 text-sm text-slate-900 placeholder-slate-400 font-medium focus:outline-none focus:border-indigo-400 transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-[4.5rem] top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <FiX size={14} />
              </button>
            )}
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all"
            >
              Search
            </button>
          </form>
        </motion.div>

        {/* ── Controls ── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-5 border-b-2 border-slate-100"
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl border-2 transition-all ${
                showFilters
                  ? "bg-indigo-50 text-indigo-600 border-indigo-200"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {showFilters ? <FiX size={14} /> : <FiSliders size={14} />}
              {showFilters ? "Close Filters" : "Filters"}
            </button>
            <span className="text-sm font-bold text-slate-500">
              {allFilteredSites.length} results
            </span>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="select-field"
            >
              <option value="rating">Highest Rated</option>
              <option value="reviews">Most Reviewed</option>
              <option value="visits">Most Visited</option>
              <option value="name">Alphabetical</option>
            </select>
            <div className="flex border-2 border-slate-200 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2.5 transition-colors ${
                  viewMode === "grid"
                    ? "bg-indigo-50 text-indigo-600"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <FiGrid size={15} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2.5 transition-colors ${
                  viewMode === "list"
                    ? "bg-indigo-50 text-indigo-600"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <FiList size={15} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── Layout ── */}
        <div className="flex gap-8">
          {/* Sidebar */}
          <AnimatePresence>
            {showFilters && (
              <motion.aside
                initial={{ opacity: 0, x: -20, width: 0 }}
                animate={{ opacity: 1, x: 0, width: 240 }}
                exit={{ opacity: 0, x: -20, width: 0 }}
                transition={{ duration: 0.25 }}
                className="hidden lg:block flex-shrink-0 overflow-hidden"
              >
                <FilterSidebar
                  minRating={minRating}
                  setMinRating={setMinRating}
                  categories={categories}
                  currentCategory={slug}
                />
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {allFilteredSites.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-12 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FiSearch size={22} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-extrabold text-slate-800 mb-1">
                  No websites found
                </h3>
                <p className="text-sm text-slate-500 mb-5">
                  Try adjusting your search or filters.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setMinRating(0);
                  }}
                  className="btn-secondary"
                >
                  Clear all filters
                </button>
              </motion.div>
            ) : (
              <>
                {/* ── Grid / List ── */}
                <div
                  className={
                    viewMode === "grid" ? `grid ${cols} gap-5` : "space-y-3"
                  }
                >
                  <AnimatePresence initial={false}>
                    {visibleSites.map((site, i) => (
                      <motion.div
                        key={site.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{
                          duration: 0.4,
                          delay: i < ITEMS_PER_PAGE ? i * 0.04 : 0,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                      >
                        <WebsiteCard
                          site={site}
                          viewMode={viewMode}
                          showRank={isTop50}
                          rank={site.globalRank || i + 1}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* ── Skeleton loaders while fetching next batch ── */}
                {loadingMore && (
                  <div
                    className={`mt-5 ${
                      viewMode === "grid" ? `grid ${cols} gap-5` : "space-y-3"
                    }`}
                  >
                    {Array.from({ length: Math.min(3, ITEMS_PER_PAGE) }).map(
                      (_, i) => (
                        <motion.div
                          key={`sk-${i}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.05 }}
                        >
                          <SkeletonCard viewMode={viewMode} />
                        </motion.div>
                      ),
                    )}
                  </div>
                )}

                {/* ── Intersection Observer trigger ── */}
                <div ref={loaderRef} className="h-4 mt-6" />

                {/* ── Loading spinner ── */}
                {loadingMore && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-center gap-2.5 py-6"
                  >
                    <FiLoader
                      size={16}
                      className="text-indigo-500 animate-spin"
                    />
                    <span className="text-sm font-semibold text-slate-500">
                      Loading more websites...
                    </span>
                  </motion.div>
                )}

                {/* ── All Loaded ── */}
                {allLoaded && visibleSites.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col items-center gap-3 py-10 mt-4"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-50 border-2 border-emerald-100 flex items-center justify-center">
                      <FiCheckCircle size={18} className="text-emerald-500" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-slate-700">
                        All {allFilteredSites.length} websites loaded
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        You've reached the end of the list
                      </p>
                    </div>
                    {/* Progress dots */}
                    <div className="flex items-center gap-1 mt-1">
                      {Array.from({
                        length: Math.min(
                          5,
                          Math.ceil(allFilteredSites.length / ITEMS_PER_PAGE),
                        ),
                      }).map((_, i) => (
                        <div
                          key={i}
                          className="w-1.5 h-1.5 rounded-full bg-slate-300"
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* ── Progress indicator (shown while scrolling) ── */}
                {!allLoaded && visibleSites.length > 0 && !loadingMore && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-3 py-6 mt-2"
                  >
                    <div className="flex-1 h-px bg-slate-200 rounded-full" />
                    <span className="text-xs font-bold text-slate-400 whitespace-nowrap">
                      {visibleSites.length} of {allFilteredSites.length}
                    </span>
                    <div className="flex-1 h-px bg-slate-200 rounded-full" />
                  </motion.div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingPage;
